import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Eye, EyeOff, Newspaper, QrCode, Briefcase } from "lucide-react";
import { SEO } from "@/components/SEO";
import { api, formatApiError } from "@/lib/api";
import { Helmet } from "react-helmet-async";
import { DownloadSettings } from "@/components/admin/DownloadSettings";
import { JobsSettings } from "@/components/admin/JobsSettings";

const SECTIONS = [
  { id: "blogs", label: "Blog posts", Icon: Newspaper },
  { id: "download", label: "App download & QR", Icon: QrCode },
  { id: "careers", label: "Careers", Icon: Briefcase },
];

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "TruCitizen", read_time: "5 min read", tags: "", keywords: "", faqs: "", published: true };
const toForm = (b) => ({ ...b, tags: b.tags.join(", "), keywords: b.keywords.join(", "), faqs: b.faqs.map((f) => `${f.question} | ${f.answer}`).join("\n") });
const toPayload = (f) => ({
  ...f,
  tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
  keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean),
  faqs: f.faqs.split("\n").map((l) => l.split("|")).filter((p) => p.length >= 2).map(([q, ...a]) => ({ question: q.trim(), answer: a.join("|").trim() })),
});

const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-widest text-[var(--muted)]">{label}</span>
    {props.rows ? (
      <textarea id={id} data-testid={id} {...props} className="mt-1 w-full rounded-xl border border-black/10 bg-white/80 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--saffron)]" />
    ) : (
      <input id={id} data-testid={id} {...props} className="mt-1 w-full rounded-xl border border-black/10 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--saffron)]" />
    )}
  </label>
);

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("tc_admin_token", data.access_token);
      onLogin(data.user);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="glass rounded-3xl p-8 max-w-md mx-auto space-y-5" data-testid="admin-login-form">
      <p className="eyebrow">Admin · Website</p>
      <h1 className="font-display text-3xl text-[var(--ink)]">TruCitizen Website CMS</h1>
      <Field label="Username" id="admin-username-input" type="text" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
      <Field label="Password" id="admin-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      <button className="btn-saffron w-full justify-center" disabled={busy} data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
    </form>
  );
};

const Editor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(f);
      const { data } = f.id ? await api.put(`/admin/blogs/${f.id}`, payload) : await api.post("/admin/blogs", payload);
      toast.success(f.id ? "Blog updated" : "Blog created");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="glass rounded-3xl p-6 sm:p-8 space-y-4" data-testid="admin-blog-editor">
      <h2 className="font-display font-bold text-2xl text-[var(--ink)]">{f.id ? "Edit blog" : "New blog"}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Title" id="editor-title" value={f.title} onChange={set("title")} required />
        <Field label="Slug (auto from title if blank)" id="editor-slug" value={f.slug} onChange={set("slug")} />
        <Field label="Cover image URL" id="editor-cover" value={f.cover_image} onChange={set("cover_image")} required />
        <Field label="Read time" id="editor-read-time" value={f.read_time} onChange={set("read_time")} />
        <Field label="Tags (comma separated)" id="editor-tags" value={f.tags} onChange={set("tags")} />
        <Field label="SEO keywords (comma separated)" id="editor-keywords" value={f.keywords} onChange={set("keywords")} />
      </div>
      <Field label="Excerpt (meta description)" id="editor-excerpt" rows={2} value={f.excerpt} onChange={set("excerpt")} required />
      <Field label="Content (HTML: <p>, <h2>, <h3>, <ul>, <div class='tldr'>, <div class='callout'>)" id="editor-content" rows={14} value={f.content} onChange={set("content")} required />
      <Field label="FAQs — one per line: Question | Answer" id="editor-faqs" rows={3} value={f.faqs} onChange={set("faqs")} />
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.published} onChange={set("published")} data-testid="editor-published" /> Published</label>
      <div className="flex gap-3">
        <button className="btn-saffron !py-2.5 !px-6 text-sm" disabled={busy} data-testid="editor-save">{busy ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="btn-ghost !py-2.5 !px-6 text-sm" data-testid="editor-cancel">Cancel</button>
      </div>
    </form>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [section, setSection] = useState("blogs");

  const load = () => api.get("/admin/blogs").then((r) => setBlogs(r.data)).catch(() => {});

  useEffect(() => {
    if (!localStorage.getItem("tc_admin_token")) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("tc_admin_token"));
  }, []);
  useEffect(() => { if (user) load(); }, [user]);

  const logout = () => { localStorage.removeItem("tc_admin_token"); setUser(null); };
  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/admin/blogs/${b.id}`);
    toast.success("Deleted");
    load();
  };
  const togglePublish = async (b) => {
    await api.put(`/admin/blogs/${b.id}`, { published: !b.published });
    load();
  };

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 sm:pt-36 pb-16 sm:pb-28" data-testid="admin-page">
      <SEO title="Admin | TruCitizen" description="TruCitizen CMS" path="/admin" />
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {!user ? (
        <Login onLogin={setUser} />
      ) : (
        <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-dashboard">
          <aside className="lg:col-span-3">
            <div className="glass rounded-3xl p-5 lg:sticky lg:top-32">
              <p className="eyebrow mb-1">Website</p>
              <p className="text-xs text-[var(--muted)] mb-4 truncate">{user.username || user.email}</p>
              <nav className="flex lg:flex-col gap-2" data-testid="admin-sections">
                {SECTIONS.map((s) => (
                  <button key={s.id} onClick={() => { setSection(s.id); setEditing(null); }} data-testid={`admin-section-${s.id}`}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-left transition-colors ${section === s.id ? "bg-[var(--ink)] text-white" : "hover:bg-black/5 text-[var(--ink)]"}`}>
                    <s.Icon size={16} /> {s.label}
                  </button>
                ))}
              </nav>
              <button onClick={logout} className="btn-ghost !py-2 !px-4 text-xs mt-6 w-full justify-center" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
            </div>
          </aside>

          <div className="lg:col-span-9">
            {section === "careers" ? (
              <>
                <div className="mb-8">
                  <p className="eyebrow mb-2">Website · Careers</p>
                  <h1 className="font-display text-4xl text-[var(--ink)]">Job openings</h1>
                  <p className="mt-2 text-sm text-[var(--ink-soft)]">Add, edit, close or reopen roles shown on the /careers page. Apply opens the Google Form in a new tab.</p>
                </div>
                <JobsSettings />
              </>
            ) : section === "download" ? (
              <>
                <div className="mb-8">
                  <p className="eyebrow mb-2">Website · App download</p>
                  <h1 className="font-display text-4xl text-[var(--ink)]">Prove It — QR &amp; store links</h1>
                  <p className="mt-2 text-sm text-[var(--ink-soft)]">Controls what visitors see in the download popup on the home page.</p>
                </div>
                <DownloadSettings />
              </>
            ) : editing ? (
              <Editor initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                  <div>
                    <p className="eyebrow mb-2">Website · Blogs</p>
                    <h1 className="font-display text-4xl text-[var(--ink)]">Blog posts</h1>
                  </div>
                  <button onClick={() => setEditing(EMPTY)} className="btn-saffron !py-2.5 !px-5 text-sm" data-testid="admin-new-blog"><Plus size={16} /> New blog</button>
                </div>
                <ul className="space-y-3" data-testid="admin-blog-list">
                  {blogs.map((b) => (
                    <li key={b.id} className="glass rounded-2xl p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`admin-blog-row-${b.slug}`}>
                      <img src={b.cover_image} alt="" className="w-20 h-14 rounded-lg object-cover" />
                      <div className="flex-1 min-w-[200px]">
                        <p className="font-bold text-[var(--ink)]">{b.title}</p>
                        <p className="text-xs font-mono text-[var(--muted)]">/blogs/{b.slug} · {b.published ? "Published" : "Draft"}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => togglePublish(b)} className="p-2 rounded-full border border-black/10 hover:bg-black/5" title={b.published ? "Unpublish" : "Publish"} data-testid={`admin-toggle-${b.slug}`}>{b.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                        <button onClick={() => setEditing(toForm(b))} className="p-2 rounded-full border border-black/10 hover:bg-black/5" data-testid={`admin-edit-${b.slug}`}><Pencil size={16} /></button>
                        <button onClick={() => remove(b)} className="p-2 rounded-full border border-black/10 hover:bg-red-50 text-red-600" data-testid={`admin-delete-${b.slug}`}><Trash2 size={16} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
